/**
 * Question Data Canvas Card Component
 *
 * Displays a strategic question with its multi-system data mapping:
 * - Question text and category
 * - Data sources with roles (Primary/Supporting/Reference)
 * - Connection points between systems
 * - Approval status and actions
 */

import React, { useMemo, useState } from 'react';
import {
  Database,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Link2,
  Star,
  ArrowRight,
  Key,
  Table2,
  Columns3,
  Edit2,
  Sparkles
} from 'lucide-react';
import { Badge } from '../../ui/Badge';
import Button from '../../ui/Button';
import Tooltip from '../../ui/Tooltip';
import type {
  QuestionDataCanvas,
  SystemDataSource,
  ConnectionPoint,
  SystemRole,
  CategorizedColumns
} from '../../../stores/analysisStore';

interface QuestionDataCanvasCardProps {
  canvas: QuestionDataCanvas;
  onEditDataSource?: (canvasId: string, systemId: string) => void;
  onAddDataSource?: (canvasId: string) => void;
  onEditConnection?: (canvasId: string, connectionId: string) => void;
  onAddConnection?: (canvasId: string) => void;
  expanded?: boolean;
  /** 1-based question index, rendered as the Q-medallion in the sticky header. */
  questionNumber?: number;
  /** True if the parent Step 3 scoring picked this canvas as the best
   *  starting point. Drives the Recommended chip + emerald accents. */
  isRecommended?: boolean;
  /** 'strong' = clear winner; 'marginal' = top but field is tight. */
  recommendationConfidence?: 'strong' | 'marginal';
  /** Per-bullet reasons shown in the chip's themed tooltip. */
  recommendationReasons?: string[];
}

const getRoleBadge = (role: SystemRole) => {
  switch (role) {
    case 'primary':
      return <Badge variant="default" className="bg-blue-600">Primary</Badge>;
    case 'supporting':
      return <Badge variant="outline" className="border-purple-500 text-purple-700">Supporting</Badge>;
    case 'reference':
      return <Badge variant="outline" className="border-gray-500 text-gray-700">Reference</Badge>;
  }
};

// Read-only AI-validation badge. Mapping approval is fully automatic — the AI
// validates mappings in the background, so canvases are never "Draft"/"In
// Review" awaiting the user. A 'flag' verdict surfaces a non-blocking
// "lower confidence" badge; everything else reads as AI-validated.
const getStatusBadge = (canvas: QuestionDataCanvas) => {
  if (canvas.validation_verdict === 'flag') {
    return (
      <Tooltip
        position="top"
        variant="light"
        maxWidth={300}
        content={
          <p className="text-xs text-neutral-600 leading-relaxed">
            The AI validated and approved this mapping but flagged it as
            lower-confidence. This is informational — it does not block you from
            continuing. You can review the data sources below if you'd like.
          </p>
        }
      >
        <Badge variant="warning" className="flex items-center gap-1 cursor-help">
          <Sparkles className="w-3 h-3" />
          AI flagged — lower confidence
        </Badge>
      </Tooltip>
    );
  }
  return (
    <Badge variant="success" className="flex items-center gap-1">
      <CheckCircle2 className="w-3 h-3" />
      AI-validated
    </Badge>
  );
};

const getCategoryColorClass = (category?: string) => {
  if (!category) return 'bg-gray-100 border-gray-300';
  const cat = category.toLowerCase();
  if (cat.includes('cost') || cat.includes('finance')) return 'bg-emerald-50 border-emerald-200';
  if (cat.includes('supply') || cat.includes('scm')) return 'bg-blue-50 border-blue-200';
  if (cat.includes('customer') || cat.includes('crm')) return 'bg-purple-50 border-purple-200';
  if (cat.includes('revenue') || cat.includes('sales')) return 'bg-amber-50 border-amber-200';
  return 'bg-gray-50 border-gray-200';
};

// Helper to count total columns in CategorizedColumns
const getTotalColumns = (columns: CategorizedColumns): number => {
  return (columns.must?.length || 0) + (columns.should?.length || 0) + (columns.could?.length || 0);
};

export const QuestionDataCanvasCard: React.FC<QuestionDataCanvasCardProps> = ({
  canvas,
  onEditDataSource,
  onAddDataSource,
  onEditConnection,
  onAddConnection,
  expanded: initialExpanded = false,
  questionNumber,
  isRecommended = false,
  recommendationConfidence,
  recommendationReasons,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  // Dedupe data sources by (system_name, vendor).  The backend canvas
  // generator can emit the same system twice under different system_ids
  // when the wizard's selected-systems list contains both a library entry
  // and an LLM-generated entry for the same product — the consultant only
  // wants to see one row per real-world system.  Earlier-wins so the
  // first occurrence's role assignment is preserved.
  const dedupedDataSources = useMemo(() => {
    const seen = new Set<string>();
    return canvas.data_sources.filter((ds) => {
      const key = `${(ds.system_name || '').toLowerCase().trim()}|${(ds.vendor || '').toLowerCase().trim()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [canvas.data_sources]);

  const primarySystem = dedupedDataSources.find(ds => ds.role === 'primary');
  const supportingSystems = dedupedDataSources.filter(ds => ds.role === 'supporting');
  const referenceSystems = dedupedDataSources.filter(ds => ds.role === 'reference');

  // Count systems flagged as "Limited Data" so we can surface the total
  // in the canvas header — easy to miss at the per-row level.
  const limitedDataCount = useMemo(() => {
    return dedupedDataSources.filter((ds) =>
      ds.tables.some(
        (t) => (t.columns.must?.length || 0) + (t.columns.should?.length || 0) + (t.columns.could?.length || 0) === 0,
      ),
    ).length;
  }, [dedupedDataSources]);

  // Outer container styling — recommended canvases get an emerald left bar
  // + halo; the rest fall back to the category-based tint so the visual
  // identity per question is preserved.
  const recommendedStrong = isRecommended && recommendationConfidence === 'strong';
  const outerClasses = recommendedStrong
    ? 'rounded-lg border-2 border-l-[6px] border-emerald-400 border-l-emerald-500 bg-emerald-50/30 ring-2 ring-emerald-100 shadow-md transition-all duration-200'
    : `rounded-lg border-2 border-l-[6px] border-l-primary-400 ${getCategoryColorClass(canvas.question_category)} transition-all duration-200`;

  return (
    <div className={outerClasses}>
      {/* Header — becomes sticky once the canvas is expanded so the
          question stays visible while the consultant scrolls through
          all its data sources.  top-0 sticks to the viewport top; z-10
          keeps it above the inner data-source rows. */}
      <div
        className={`p-4 cursor-pointer ${isExpanded ? 'sticky top-0 z-10 bg-white/95 backdrop-blur-sm rounded-t-md shadow-sm' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          {/* Q-medallion — gives the section a single dominant visual
              anchor so the sticky bar reads "this is Q3" at a glance. */}
          {questionNumber !== undefined && (
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full ${recommendedStrong ? 'bg-emerald-600' : 'bg-primary-600'} text-white flex items-center justify-center font-bold text-sm shadow-sm`}
              // The medallion number is positional. Expose the real
              // questionId on hover so "Q1" can be traced to the node it
              // actually refers to when debugging or reporting an issue.
              title={canvas.question_id ? `${canvas.question_id}` : undefined}
            >
              Q{questionNumber}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {canvas.question_category && (
                <Badge variant="outline" className="text-xs">
                  {canvas.question_category}
                </Badge>
              )}
              {getStatusBadge(canvas)}
              {/* Recommended chip — uses the same emerald/amber pattern as
                  Step 0 / Step 2 with a themed tooltip explaining why. */}
              {isRecommended && recommendationReasons && recommendationReasons.length > 0 && (
                <Tooltip
                  position="top"
                  variant="light"
                  maxWidth={420}
                  content={
                    <div className="space-y-2">
                      <p className="font-semibold text-neutral-900">
                        {recommendationConfidence === 'strong'
                          ? 'Recommended starting point'
                          : "Top scorer, but it's a close call"}
                      </p>
                      <p className="text-xs text-neutral-600 leading-relaxed">
                        {recommendationConfidence === 'strong'
                          ? "This canvas scored higher than the others on the signals below. Approve it first — its mapping is the most ready for Step 4 schema generation."
                          : "This canvas narrowly beat the others on the signals below.  Worth comparing against the alternatives before committing — the gap is small."}
                      </p>
                      <ul className="text-xs text-neutral-700 list-disc list-outside ml-4 space-y-1">
                        {recommendationReasons.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  <Badge
                    className={
                      recommendationConfidence === 'strong'
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1 cursor-help'
                        : 'bg-amber-50 text-amber-800 border border-amber-300 flex items-center gap-1 cursor-help'
                    }
                  >
                    <Sparkles className="w-3 h-3" />
                    {recommendationConfidence === 'strong' ? 'Recommended' : 'Top match'}
                  </Badge>
                </Tooltip>
              )}
              {/* Limited Data summary — elevates the per-row warning to
                  the header so the consultant sees the issue without
                  expanding the canvas.  Tooltip explains the downstream
                  implication for Step 4 schema generation. */}
              {limitedDataCount > 0 && (
                <Tooltip
                  position="top"
                  variant="light"
                  maxWidth={320}
                  content={
                    <div className="space-y-1">
                      <p className="font-semibold text-neutral-900">
                        {limitedDataCount} system{limitedDataCount === 1 ? ' has' : 's have'} limited data
                      </p>
                      <p className="text-xs text-neutral-600 leading-relaxed">
                        At least one of the mapped tables on {limitedDataCount === 1 ? 'this system' : 'these systems'} has no column data, which means Step 4 schema generation may produce an incomplete DDL.  Consider researching {limitedDataCount === 1 ? 'this system' : 'these systems'} or removing the empty tables before approving.
                      </p>
                    </div>
                  }
                >
                  <Badge variant="warning" className="flex items-center gap-1 cursor-help">
                    <AlertTriangle className="w-3 h-3" />
                    {limitedDataCount} limited
                  </Badge>
                </Tooltip>
              )}
            </div>
            <h3 className="text-base font-bold text-neutral-900 mb-2 leading-snug">
              {canvas.question_text}
            </h3>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Database className="w-3 h-3" />
                {dedupedDataSources.length} system{dedupedDataSources.length !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Table2 className="w-3 h-3" />
                {canvas.total_tables} table{canvas.total_tables !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Columns3 className="w-3 h-3" />
                {canvas.total_columns} column{canvas.total_columns !== 1 ? 's' : ''}
              </span>
              {canvas.total_connections > 0 && (
                <span className="flex items-center gap-1 text-blue-600">
                  <Link2 className="w-3 h-3" />
                  {canvas.total_connections} connection{canvas.total_connections !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <button className="p-1 hover:bg-white/50 rounded flex-shrink-0">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200">
          {/* Data Sources Section */}
          <div className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">Data Sources</h4>
              {onAddDataSource && canvas.status === 'draft' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddDataSource(canvas.canvas_id)}
                >
                  + Add System
                </Button>
              )}
            </div>

            {/* Primary System */}
            {primarySystem && (
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  <Star className="w-3 h-3 text-blue-500" />
                  PRIMARY SOURCE
                </div>
                <DataSourceCard
                  dataSource={primarySystem}
                  canEdit={canvas.status === 'draft' && !!onEditDataSource}
                  onEdit={() => onEditDataSource?.(canvas.canvas_id, primarySystem.system_id)}
                />
              </div>
            )}

            {/* Supporting Systems */}
            {supportingSystems.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-500 mb-1">SUPPORTING</div>
                <div className="space-y-2">
                  {supportingSystems.map(ds => (
                    <DataSourceCard
                      key={ds.system_id}
                      dataSource={ds}
                      canEdit={canvas.status === 'draft' && !!onEditDataSource}
                      onEdit={() => onEditDataSource?.(canvas.canvas_id, ds.system_id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Reference Systems */}
            {referenceSystems.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-500 mb-1">REFERENCE</div>
                <div className="space-y-2">
                  {referenceSystems.map(ds => (
                    <DataSourceCard
                      key={ds.system_id}
                      dataSource={ds}
                      canEdit={canvas.status === 'draft' && !!onEditDataSource}
                      onEdit={() => onEditDataSource?.(canvas.canvas_id, ds.system_id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Connection Points Section */}
          {(canvas.connection_points.length > 0 || canvas.status === 'draft') && (
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-blue-500" />
                  Cross-System Connections
                </h4>
                {onAddConnection && canvas.status === 'draft' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAddConnection(canvas.canvas_id)}
                  >
                    + Add Connection
                  </Button>
                )}
              </div>

              {canvas.connection_points.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No cross-system connections defined</p>
              ) : (
                <div className="space-y-2">
                  {canvas.connection_points.map(cp => (
                    <ConnectionPointCard
                      key={cp.connection_id}
                      connection={cp}
                      canEdit={canvas.status === 'draft' && !!onEditConnection}
                      onEdit={() => onEditConnection?.(canvas.canvas_id, cp.connection_id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Consultant Notes */}
          {canvas.consultant_notes && (
            <div className="pt-2 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-1">Consultant Notes</div>
              <p className="text-sm text-gray-700 bg-white/50 p-2 rounded">{canvas.consultant_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Sub-component: Data Source Card
interface DataSourceCardProps {
  dataSource: SystemDataSource;
  canEdit: boolean;
  onEdit: () => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({ dataSource, canEdit, onEdit }) => {
  const [showTables, setShowTables] = useState(false);
  const [expandedTableIdx, setExpandedTableIdx] = useState<number | null>(null);

  // Check if any table has no columns
  const hasEmptyTables = dataSource.tables.some(
    t => (t.columns.must?.length || 0) + (t.columns.should?.length || 0) + (t.columns.could?.length || 0) === 0
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-400" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900">{dataSource.system_name}</span>
              {getRoleBadge(dataSource.role)}
              {hasEmptyTables && (
                <Badge variant="warning" className="text-xs flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Limited Data
                </Badge>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {dataSource.vendor} {dataSource.functional_area && `• ${dataSource.functional_area}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            onClick={() => setShowTables(!showTables)}
          >
            {dataSource.tables.length} table{dataSource.tables.length !== 1 ? 's' : ''}
            {showTables ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {canEdit && (
            <button
              className="p-1 hover:bg-gray-100 rounded"
              onClick={onEdit}
            >
              <Edit2 className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {dataSource.role_description && (
        <p className="text-xs text-gray-500 mt-2 italic">{dataSource.role_description}</p>
      )}

      {/* Tables Expansion */}
      {showTables && dataSource.tables.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          {dataSource.tables.map((table, idx) => {
            const totalColumns = (table.columns.must?.length || 0) + (table.columns.should?.length || 0) + (table.columns.could?.length || 0);
            const isExpanded = expandedTableIdx === idx;
            const isEmpty = totalColumns === 0;

            return (
              <div key={idx} className={`text-xs ${isEmpty ? 'bg-amber-50 rounded p-2' : ''}`}>
                <div
                  className="flex items-center gap-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
                  onClick={() => setExpandedTableIdx(isExpanded ? null : idx)}
                >
                  <Table2 className="w-3 h-3" />
                  <span className="font-mono flex-1">{table.table_name}</span>
                  {table.module && (
                    <span className="text-gray-400">({table.module})</span>
                  )}
                  {isEmpty && (
                    <span className="text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      No columns
                    </span>
                  )}
                  {!isEmpty && (
                    <span className="text-gray-400">{totalColumns} cols</span>
                  )}
                  {!isEmpty && (isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                </div>

                {/* Column counts summary */}
                <div className="ml-5 mt-1 flex items-center gap-3 text-gray-500">
                  <span className="flex items-center gap-1">
                    <Key className="w-2.5 h-2.5 text-red-400" />
                    {table.columns.must?.length || 0} must
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 text-amber-400" />
                    {table.columns.should?.length || 0} should
                  </span>
                  <span className="flex items-center gap-1">
                    <Columns3 className="w-2.5 h-2.5 text-gray-400" />
                    {table.columns.could?.length || 0} could
                  </span>
                </div>

                {/* Expanded Column Details */}
                {isExpanded && !isEmpty && (
                  <div className="ml-5 mt-2 space-y-2 border-l-2 border-gray-200 pl-3">
                    {/* Must Have Columns */}
                    {table.columns.must && table.columns.must.length > 0 && (
                      <div>
                        <div className="font-medium text-red-700 mb-1 flex items-center gap-1">
                          <Key className="w-3 h-3" />
                          Must Have ({table.columns.must.length})
                        </div>
                        <div className="space-y-0.5">
                          {table.columns.must.map((col, colIdx) => (
                            <div key={colIdx} className="flex items-center gap-2 text-gray-600">
                              <span className="font-mono text-gray-800">{col.column_name}</span>
                              <span className="text-gray-400">—</span>
                              <span className="text-gray-500">{col.reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Should Have Columns */}
                    {table.columns.should && table.columns.should.length > 0 && (
                      <div>
                        <div className="font-medium text-amber-700 mb-1 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Should Have ({table.columns.should.length})
                        </div>
                        <div className="space-y-0.5">
                          {table.columns.should.map((col, colIdx) => (
                            <div key={colIdx} className="flex items-center gap-2 text-gray-600">
                              <span className="font-mono text-gray-800">{col.column_name}</span>
                              <span className="text-gray-400">—</span>
                              <span className="text-gray-500">{col.reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Could Have Columns */}
                    {table.columns.could && table.columns.could.length > 0 && (
                      <div>
                        <div className="font-medium text-gray-600 mb-1 flex items-center gap-1">
                          <Columns3 className="w-3 h-3" />
                          Could Have ({table.columns.could.length})
                        </div>
                        <div className="space-y-0.5">
                          {table.columns.could.slice(0, 10).map((col, colIdx) => (
                            <div key={colIdx} className="flex items-center gap-2 text-gray-600">
                              <span className="font-mono text-gray-800">{col.column_name}</span>
                              <span className="text-gray-400">—</span>
                              <span className="text-gray-500">{col.reason}</span>
                            </div>
                          ))}
                          {table.columns.could.length > 10 && (
                            <div className="text-gray-400 italic">
                              + {table.columns.could.length - 10} more columns...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Empty table warning */}
                {isEmpty && (
                  <div className="ml-5 mt-1 text-amber-600 text-xs italic">
                    No column data available. System may need research.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No tables warning */}
      {showTables && dataSource.tables.length === 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3 text-xs text-amber-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          No tables mapped for this system. Consider researching this system for data mapping.
        </div>
      )}
    </div>
  );
};

// Sub-component: Connection Point Card
interface ConnectionPointCardProps {
  connection: ConnectionPoint;
  canEdit: boolean;
  onEdit: () => void;
}

const ConnectionPointCard: React.FC<ConnectionPointCardProps> = ({ connection, canEdit, onEdit }) => {
  const getConnectionTypeBadge = () => {
    switch (connection.connection_type) {
      case 'direct_key':
        return <Badge variant="success" className="text-xs">Direct Key</Badge>;
      case 'derived_key':
        return <Badge variant="warning" className="text-xs">Derived</Badge>;
      case 'semantic':
        return <Badge variant="info" className="text-xs">Semantic</Badge>;
      case 'temporal':
        return <Badge variant="outline" className="text-xs">Temporal</Badge>;
    }
  };

  return (
    <div className="bg-white/70 rounded border border-gray-200 p-2 text-xs">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-1 text-gray-700">
            <span className="font-medium">{connection.source_system_name}</span>
            <span className="text-gray-400">.</span>
            <span className="font-mono text-blue-600">{connection.source_table}</span>
            <span className="text-gray-400">.</span>
            <span className="font-mono text-blue-800">{connection.source_column}</span>
          </div>
        </div>
        <ArrowRight className="w-3 h-3 text-gray-400" />
        <div className="flex-1">
          <div className="flex items-center gap-1 text-gray-700">
            <span className="font-medium">{connection.target_system_name}</span>
            <span className="text-gray-400">.</span>
            <span className="font-mono text-purple-600">{connection.target_table}</span>
            <span className="text-gray-400">.</span>
            <span className="font-mono text-purple-800">{connection.target_column}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getConnectionTypeBadge()}
          <span className="text-gray-400">{connection.cardinality}</span>
          {connection.validated && (
            <CheckCircle2 className="w-3 h-3 text-green-500" />
          )}
          {canEdit && (
            <button
              className="p-1 hover:bg-gray-100 rounded"
              onClick={onEdit}
            >
              <Edit2 className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>
      {connection.description && (
        <p className="text-gray-500 mt-1 italic">{connection.description}</p>
      )}
    </div>
  );
};

export default QuestionDataCanvasCard;
